import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  IGFEOrderPricingCalculationRequest,
  IGFEOrderPricingRequest,
} from "types/Order/Order";
import {
  IGetPrecalculatedAllowablesParams,
  IPrecalculatedAllowablesResponse,
} from "types/precalculatedAllowables.types";
import {
  ICaseRateRuleset,
  ICaseRateRulesetCreateRequest,
  ICaseRateRulesetUpdateRequest,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRuleset";
import {
  IAllCaseRateRulesetResponse,
  ICaseRateRulesetResponse,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRulesetResponse";

import { api } from ".";

const accountQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    pricing: builder.mutation<any, IGFEOrderPricingRequest>({
      query: (pricing) => ({
        url: "pricing",
        method: "POST",
        body: pricing,
      }),
    }),
    getEstimateCalculation: builder.query<
      any,
      IGFEOrderPricingCalculationRequest
    >({
      query: (pricing) => ({
        url: "pricing/calculateEstimate",
        method: "POST",
        body: pricing,
      }),
    }),
    getCaseRateRulesets: builder.query<
      IAllCaseRateRulesetResponse,
      | (IRulesetListParams & {
          providerId?: string;
          payerId?: number;
        })
      | null
      | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.applicability?.length) {
          search.set("applicability", params.applicability.join(","));
        } else if (params?.isDefault !== undefined) {
          search.set("isDefault", String(params.isDefault));
        }
        if (params?.providerId) {
          search.set("providerId", params.providerId);
        }
        if (params?.payerId !== undefined && params?.payerId !== null) {
          search.set("payerId", String(params.payerId));
        }
        const qs = search.toString();
        return `pricing/caseRateRulesets${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["CaseRateRulesets"],
    }),
    getCaseRateRulesetById: builder.query<
      ICaseRateRulesetResponse,
      string | undefined
    >({
      query: (id: string) => `pricing/caseRateRulesets/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "CaseRateRulesets", id: id ?? "LIST" },
      ],
    }),
    createCaseRateRuleset: builder.mutation<
      ICaseRateRuleset,
      ICaseRateRulesetCreateRequest
    >({
      query: (newRuleset) => ({
        url: "pricing/caseRateRuleset",
        method: "POST",
        body: newRuleset,
      }),
      invalidatesTags: ["CaseRateRulesets", "ContractFeeSchedules"],
    }),
    updateCaseRateRuleset: builder.mutation<
      ICaseRateRuleset,
      ICaseRateRulesetUpdateRequest
    >({
      query: (updatedRuleset) => ({
        url: `pricing/caseRateRuleset/${updatedRuleset.id}`,
        method: "PUT",
        body: updatedRuleset,
      }),
      invalidatesTags: [
        "CaseRateRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    deleteCaseRateRuleset: builder.mutation<any, string>({
      query: (id) => ({
        url: `pricing/caseRateRuleset/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "CaseRateRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    cloneCaseRateAsDefault: builder.mutation<ICaseRateRulesetResponse, string>({
      query: (id) => ({
        url: `pricing/caseRateRuleset/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["CaseRateRulesets"],
    }),
    copyCaseRateToContract: builder.mutation<
      ICaseRateRulesetResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `pricing/caseRateRuleset/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CaseRateRulesets", "ContractFeeSchedules"],
    }),
    getCaseRatePropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `pricing/caseRateRuleset/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `caseRate:${id}` },
      ],
    }),
    applyCaseRatePropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `pricing/caseRateRuleset/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "CaseRateRulesets",
        "ContractFeeSchedules",
        { type: "DefaultRulePropagation", id: `caseRate:${id}` },
      ],
    }),
    getPrecalculatedAllowables: builder.query<
      IPrecalculatedAllowablesResponse,
      IGetPrecalculatedAllowablesParams
    >({
      query: ({ accountId, payerId, caseSignature, dateOfService }) => {
        const params = new URLSearchParams({
          accountId,
          payerId: payerId.toString(),
          caseSignature,
        });
        // CLE-2299: scope the lookup to the claim's service date so we don't return a
        // stale canonical (NULL-serviceDate) row that disagrees with the claim's own
        // expectedAmount after a config-driven repricing.
        if (dateOfService) params.append("dateOfService", dateOfService);
        return {
          url: `pricing/precalculated-allowables?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["PrecalculatedAllowables"],
    }),
  }),
});

export const {
  usePricingMutation,
  useGetEstimateCalculationQuery,
  useLazyGetEstimateCalculationQuery,
  useGetCaseRateRulesetsQuery,
  useCreateCaseRateRulesetMutation,
  useDeleteCaseRateRulesetMutation,
  useUpdateCaseRateRulesetMutation,
  useGetCaseRateRulesetByIdQuery,
  useCloneCaseRateAsDefaultMutation,
  useCopyCaseRateToContractMutation,
  useGetCaseRatePropagationPreviewQuery,
  useLazyGetCaseRatePropagationPreviewQuery,
  useApplyCaseRatePropagationMutation,
  useGetPrecalculatedAllowablesQuery,
} = accountQueries;
